import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    IconButton,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import CopyrightIcon from '@mui/icons-material/Copyright';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const CopyrightDialog = ({ open, onClose, onAgree }) => {
    const theme = useTheme();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                },
            }}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(10, 18, 41, 0.75)',
                    backdropFilter: 'blur(4px)',
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'linear-gradient(135deg, #0a1229 0%, #1a2847 100%)',
                    color: 'white',
                    py: 3,
                    px: 3,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 150,
                        height: 150,
                        background: 'rgba(255, 94, 28, 0.15)',
                        borderRadius: '50%',
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
                    <CopyrightIcon sx={{ fontSize: '1.8rem' }} />
                    <Typography variant="h5" component="span" sx={{ fontWeight: 700 }}>
                        Copyright Agreement
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: 'white',
                        position: 'relative',
                        zIndex: 1,
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ mt: 2, px: 3 }}>
                <Box
                    sx={{
                        mb: 3,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        p: 2.5,
                        backgroundColor: '#fff8f3',
                        borderRadius: 2,
                        border: '1px solid #ffe0cc',
                    }}
                >
                    <WarningAmberIcon sx={{ mt: 0.5, color: '#ff5e1c', fontSize: '1.5rem' }} />
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0a1229' }}>
                            Important Notice
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Please read and understand the following copyright terms before proceeding.
                        </Typography>
                    </Box>
                </Box>

                <Typography variant="body1" paragraph>
                    This website's content, including text, images, graphics, videos, and audio materials, is protected under copyright law.
                    The copyright for all content, unless specified otherwise, belongs to Louisiana Contracting Services Inc. All rights are
                    reserved.
                </Typography>

                <Typography variant="body1" paragraph sx={{ color: theme.palette.error.main, fontWeight: 'medium' }}>
                    Unauthorized reproduction, distribution, or modification of any content on this website is strictly forbidden and may
                    lead to legal consequences.
                </Typography>

                <Box sx={{ my: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#0a1229', fontWeight: 700, mb: 2 }}>
                        Prohibited Actions Without Prior Written Permission:
                    </Typography>
                    <List sx={{ bgcolor: '#fafafa', borderRadius: 2, py: 1 }}>
                        {[
                            'Commercial Distribution: Reproducing or distributing our content for any commercial purpose.',
                            'Promotional Use: Utilizing our content in promotional or marketing materials.',
                            'Derivative Works: Creating works derived from our content.',
                            'Altering Notices: Removing or altering any copyright or other proprietary notices from our content.',
                        ].map((text, index) => (
                            <ListItem key={index} sx={{ py: 1.5 }}>
                                <ListItemIcon>
                                    <BlockIcon sx={{ color: '#ff5e1c' }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={text}
                                    primaryTypographyProps={{
                                        sx: { fontSize: '0.95rem' }
                                    }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body1" paragraph>
                    For permissions or inquiries about copyright, please reach out to Louisiana Contractors Licensing Service Inc.
                </Typography>

                <Typography variant="body1" paragraph sx={{ fontWeight: 'medium' }}>
                    We are committed to enforcing our copyright and protecting our intellectual property rights rigorously. Your respect for
                    our copyrights is appreciated.
                </Typography>

                <Paper
                    elevation={0}
                    sx={{
                        p: 2.5,
                        background: 'linear-gradient(135deg, #0a1229 0%, #1a2847 100%)',
                        borderRadius: 2,
                        mt: 2,
                    }}
                >
                    <Typography variant="subtitle2" align="center" sx={{ color: '#fff', fontWeight: 600 }}>
                        Louisiana Contractors Licensing Service Inc. Copyright 2024
                    </Typography>
                </Paper>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 3, bgcolor: '#fafafa', gap: 2 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.25,
                        borderColor: '#0a1229',
                        color: '#0a1229',
                        borderWidth: 2,
                        fontWeight: 600,
                        '&:hover': {
                            borderColor: '#0a1229',
                            backgroundColor: 'rgba(10, 18, 41, 0.04)',
                            borderWidth: 2,
                        },
                    }}
                >
                    Decline
                </Button>
                <Button
                    onClick={onAgree}
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    sx={{
                        borderRadius: 2,
                        px: 4,
                        py: 1.25,
                        backgroundColor: '#ff5e1c',
                        color: '#fff',
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(255, 94, 28, 0.25)',
                        '&:hover': {
                            backgroundColor: '#e54e0c',
                            boxShadow: '0 6px 16px rgba(255, 94, 28, 0.35)',
                            transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    I Agree
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CopyrightDialog;
